import SVG from 'svg.js';
import {
    translateFromCanvas, translateFromSVG, translateToCanvas, translateToSVG,
} from './shared';
import { Geometry } from './canvasModel';
import Crosshair from './crosshair';

const CONFIG = {
    SNAP_POINT: {
        MIN_RADIUS: 18,
        MAX_RADIUS: 60,
        ANIMATION_DURATION: 300, // добавляем константу для времени анимации
        MOUSE_MOVE_DEBOUNCE_TIMEOUT: 5, // добавляем константу для времени анимации
    },
};

interface Point {
    readonly x: number,
    readonly y: number
}

interface SnapPoint extends Point {
    element: SVG.Element,
    animate: SVG.Animation
}

interface MousePosition {
    readonly clientX: number,
    readonly clientY: number,
}

interface SnapPointMousePosition {
    readonly mousePosition: MousePosition,
    readonly positionChange: boolean
}

type SnapLineAnchorPoint = undefined | {
    readonly point: Point,
    readonly type: 'horizontal' | 'vertical'
};

type SnappedMouseEvent = {
    readonly event: MouseEvent,
    readonly positionChange: boolean,
};

export interface SnapPointsHandler {
    transform(geometry: Geometry): void;

    release(): void;

    updateSnapLineAnchorPoint(): SnapPointsHandler;

    adjustMouseCoordinatesForSnapping(): Point;

    setSourceMouseEvent(e: MouseEvent): SnapPointsHandler;

    updateAxisAlignedSnappingState(): SnapPointsHandler;

    moveCrosshairToSnap(): SnapPointsHandler;

    getSnappedMouseEvent(eventType?: string): SnappedMouseEvent;
    snapPointEnabled(flag?: boolean): boolean;
    snapLineByAnnotationVertexEnabled(flag?: boolean): boolean;
}

type SnapPointProvider = (point: Point) => Point | undefined;
type SnapLineAnchorVertexProvider = (point: Point) => SnapLineAnchorPoint;
type CrosshairProvider = () => Crosshair | undefined;
type DrawingAnnotationStateProvider = () => boolean;

export class SnapPointsHandlerImpl implements SnapPointsHandler {
    private readonly canvas: SVG.Container;
    private geometry: Geometry;
    private readonly isDrawingAnnotation: DrawingAnnotationStateProvider;

    private minSnapPointRadius = CONFIG.SNAP_POINT.MIN_RADIUS;
    private maxSnapPointRadius = CONFIG.SNAP_POINT.MAX_RADIUS;

    private currentVertexSnapPoint: SnapPoint | null;
    private currentSnapLineAnchorVertex: SnapPoint | null;

    private readonly snapPointProvider: SnapPointProvider;
    private readonly snapLineAnchorVertexProvider: SnapLineAnchorVertexProvider;

    private readonly crosshairProvider: CrosshairProvider;

    private orthogonalAlignmentVertexPosition: SnapPointMousePosition | null;
    private snapPointPosition: SnapPointMousePosition | null;

    private sourceMouseEvent: MouseEvent | null;
    private snappedMouseEvent: SnappedMouseEvent | null;

    private mouseMoveDebounceTimeout:number | null;

    private enabledSnapPoint = false;
    private enabledSnapLineByAnnotationVertex = false;

    constructor(
        canvas: SVG.Container,
        geometry: Geometry,
        snapPointProvider: SnapPointProvider,
        snapLineAnchorVertexProvider: SnapLineAnchorVertexProvider,
        drawingAnnotationStateProvider: DrawingAnnotationStateProvider,
        crosshairProvider: CrosshairProvider,
    ) {
        this.mouseMoveDebounceTimeout = null;
        this.canvas = canvas;
        this.geometry = geometry;
        this.isDrawingAnnotation = drawingAnnotationStateProvider;
        this.currentVertexSnapPoint = null;
        this.currentSnapLineAnchorVertex = null;
        this.snapPointProvider = snapPointProvider;
        this.snapLineAnchorVertexProvider = snapLineAnchorVertexProvider;
        this.crosshairProvider = crosshairProvider;

        this.canvas.on('mousemove.snapPoints', this.handleMouseMove.bind(this));
    }

    snapLineByAnnotationVertexEnabled(flag?: boolean): boolean {
        if (flag !== undefined) {
            this.enabledSnapLineByAnnotationVertex = flag;
        }
        return this.enabledSnapLineByAnnotationVertex;
    }

    snapPointEnabled(flag?: boolean): boolean {
        if (flag !== undefined) {
            this.enabledSnapPoint = flag;
        }
        return this.enabledSnapPoint;
    }

    public setSourceMouseEvent(e: MouseEvent): SnapPointsHandler {
        this.sourceMouseEvent = e;
        this.snappedMouseEvent = null;
        this.orthogonalAlignmentVertexPosition = null;
        this.snapPointPosition = null;
        return this;
    }

    public getSnappedMouseEvent(eventType?: string): SnappedMouseEvent {
        if (this.snappedMouseEvent === null ||
            (eventType !== undefined && this.snappedMouseEvent.event.type !== eventType)) {
            this.snappedMouseEvent = this.createSnappedMouseEvent();
        }

        return this.snappedMouseEvent;
    }

    public moveCrosshairToSnap(): SnapPointsHandler {
        const e = this.getSnappedMouseEvent();
        const crosshair = this.crosshairProvider();
        if (crosshair && e.positionChange && this.enabledSnapLineByAnnotationVertex) {
            const [crosshairX, crosshairY] = translateToSVG(
                (this.canvas.node as any) as SVGSVGElement,
                [e.event.clientX, e.event.clientY],
            );
            crosshair.move(crosshairX, crosshairY);
        }
        return this;
    }

    public updateAxisAlignedSnappingState(): SnapPointsHandler {
        if (this.sourceMouseEvent.ctrlKey) {
            this.hideVertexAlignmentLine();
        }

        return this;
    }

    public updateSnapLineAnchorPoint(): SnapPointsHandler {
        if (!this.enabledSnapLineByAnnotationVertex) {
            return this;
        }

        if (this.isDrawingAnnotation()) {
            const axisAlignedSnapPosition = this.computeAxisAlignedSnapPosition();
            if (this.sourceMouseEvent.ctrlKey && axisAlignedSnapPosition.positionChange) {
                this.showSnapLineAnchorPoint();
            } else {
                this.hideVertexAlignmentLine();
            }
        }

        return this;
    }

    public adjustMouseCoordinatesForSnapping(): Point {
        let { clientX, clientY } = this.sourceMouseEvent;
        if (this.isDrawingAnnotation() && this.enabledSnapLineByAnnotationVertex) {
            const axisAlignedSnapPosition = this.computeAxisAlignedSnapPosition();
            if (this.sourceMouseEvent.ctrlKey && axisAlignedSnapPosition.positionChange) {
                clientX = axisAlignedSnapPosition.mousePosition.clientX;
                clientY = axisAlignedSnapPosition.mousePosition.clientY;
            }
        }

        return {
            x: clientX,
            y: clientY,
        };
    }

    public transform(geometry: Geometry): void {
        this.geometry = geometry;
    }

    public release(): void {
        this.hideVertexAlignmentLine();
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.enabledSnapPoint) {
            return;
        }
        if (this.mouseMoveDebounceTimeout !== null) {
            window.clearTimeout(this.mouseMoveDebounceTimeout);
        }

        this.mouseMoveDebounceTimeout = window.setTimeout(() => {
            const magnetPoint = this.getSnapPoint(e);
            this.updateVertexSnapPointVisibility(magnetPoint);
        }, CONFIG.SNAP_POINT.MOUSE_MOVE_DEBOUNCE_TIMEOUT);
    }

    private createSnappedMouseEvent(): SnappedMouseEvent {
        if (this.sourceMouseEvent === null) {
            throw new Error('Error create proxy event. Source event is not set');
        }

        const newPosition = this.sourceMouseEvent.ctrlKey ?
            this.computeAxisAlignedSnapPosition() :
            this.computeSnapPointPosition();

        const snappedMouseEvent = new MouseEvent(this.sourceMouseEvent.type);
        Object.defineProperties(snappedMouseEvent, {
            clientX: { value: newPosition.mousePosition.clientX, writable: false },
            clientY: { value: newPosition.mousePosition.clientY, writable: false },
        });

        return {
            event: snappedMouseEvent,
            positionChange: newPosition.positionChange,
        };
    }

    private mouseCoordinateToAnnotationsCoordinate(e: MouseEvent): Point {
        const { offset } = this.geometry;

        const [x, y] = translateFromCanvas(
            offset,
            translateToSVG((this.canvas.node as any) as SVGSVGElement, [e.clientX, e.clientY]),
        );

        return { x, y };
    }

    private getSnapPoint(e: MouseEvent): Point | undefined {
        return this.snapPointProvider(this.mouseCoordinateToAnnotationsCoordinate(e));
    }

    private getSnapLineAnchorPoint(e: MouseEvent): SnapLineAnchorPoint {
        return this.snapLineAnchorVertexProvider(this.mouseCoordinateToAnnotationsCoordinate(e));
    }

    private computeSnapPointPosition(): SnapPointMousePosition {
        if (this.snapPointPosition !== null) {
            return this.snapPointPosition;
        }

        if (this.enabledSnapPoint === false) {
            return {
                mousePosition: { clientX: this.sourceMouseEvent.clientX, clientY: this.sourceMouseEvent.clientY },
                positionChange: false,
            };
        }

        const snapPoint = this.getSnapPoint(this.sourceMouseEvent);

        if (snapPoint === undefined) {
            return {
                mousePosition: { clientX: this.sourceMouseEvent.clientX, clientY: this.sourceMouseEvent.clientY },
                positionChange: false,
            };
        }

        const [clientX, clientY] = translateFromSVG(
            (this.canvas.node as any) as SVGSVGElement,
            translateToCanvas(this.geometry.offset, [snapPoint.x, snapPoint.y]),
        );

        this.snapPointPosition = {
            mousePosition: { clientX, clientY },
            positionChange: true,
        };

        return this.snapPointPosition;
    }

    private computeAxisAlignedSnapPosition(): SnapPointMousePosition {
        if (this.orthogonalAlignmentVertexPosition !== null) {
            return this.orthogonalAlignmentVertexPosition;
        }

        if (this.enabledSnapLineByAnnotationVertex === false) {
            return {
                mousePosition: { clientX: this.sourceMouseEvent.clientX, clientY: this.sourceMouseEvent.clientY },
                positionChange: false,
            };
        }

        const snapLineAnchorPoint = this.getSnapLineAnchorPoint(this.sourceMouseEvent);
        if (snapLineAnchorPoint === undefined) {
            return {
                mousePosition: { clientX: this.sourceMouseEvent.clientX, clientY: this.sourceMouseEvent.clientY },
                positionChange: false,
            };
        }

        let [clientX, clientY] = translateFromSVG(
            (this.canvas.node as any) as SVGSVGElement,
            translateToCanvas(this.geometry.offset, [snapLineAnchorPoint.point.x, snapLineAnchorPoint.point.y]),
        );

        if (snapLineAnchorPoint.type === 'horizontal') {
            clientX = this.sourceMouseEvent.clientX;
        } else {
            clientY = this.sourceMouseEvent.clientY;
        }
        this.orthogonalAlignmentVertexPosition = {
            mousePosition: { clientX, clientY },
            positionChange: true,
        };

        return this.orthogonalAlignmentVertexPosition;
    }

    private hidePreviousVertexSnapPoint(): void {
        if (this.currentVertexSnapPoint === null) {
            return;
        }
        this.currentVertexSnapPoint.animate.stop();
        this.currentVertexSnapPoint.animate = undefined;

        this.currentVertexSnapPoint.element.remove();
        this.currentVertexSnapPoint.element = undefined;
        this.currentVertexSnapPoint = null;
    }

    private updateVertexSnapPointVisibility(point: Point | undefined): void {
        if (point === undefined) {
            this.hideCurrentVertexSnapPointIfExists();
            return;
        }
        if (this.shouldUpdateVertexSnapPoint(point)) {
            this.hideCurrentVertexSnapPointIfExists();
            this.drawVertexSnapPoint(point);
        }
    }

    private hideCurrentVertexSnapPointIfExists(): void {
        if (this.currentVertexSnapPoint !== null) {
            this.hidePreviousVertexSnapPoint();
        }
    }

    private shouldUpdateVertexSnapPoint(newPoint: Point): boolean {
        if (!this.currentVertexSnapPoint) {
            return true;
        }

        return this.currentVertexSnapPoint.x !== newPoint.x &&
            this.currentVertexSnapPoint.y !== newPoint.y;
    }

    private drawVertexSnapPoint(point: Point): void {
        if (!this.isDrawingAnnotation()) {
            return;
        }
        const { offset } = this.geometry;
        const vertexSnapPoint = translateToCanvas(offset, [point.x, point.y]);

        const circle = this.canvas.nested()
            .circle()
            .radius(this.maxSnapPointRadius)
            .center(vertexSnapPoint[0], vertexSnapPoint[1])
            .fill('none')
            .stroke({ color: 'green' })
            .attr({
                'fill-opacity': 1,
                'stroke-width': 1 / this.geometry.scale,
            });

        this.currentVertexSnapPoint = {
            x: point.x,
            y: point.y,
            element: circle,
            animate: this.createAnimate(circle),
        };
    }

    private showSnapLineAnchorPoint(): void {
        const snapLineAnchorPoint = this.getSnapLineAnchorPoint(this.sourceMouseEvent);
        if (snapLineAnchorPoint === undefined) {
            return;
        }
        const point = translateToCanvas(
            this.geometry.offset,
            [snapLineAnchorPoint.point.x, snapLineAnchorPoint.point.y],
        );

        if (this.currentSnapLineAnchorVertex) {
            if (this.currentSnapLineAnchorVertex.x === point[0] && this.currentSnapLineAnchorVertex.y === point[1]) {
                return;
            }
            this.hideVertexAlignmentLine();
        }

        const circle = this.canvas.nested()
            .circle()
            .radius(this.maxSnapPointRadius)
            .center(point[0], point[1])
            .fill('none')
            .stroke({ color: 'red' })
            .attr({
                'fill-opacity': 1,
                'stroke-width': 1 / this.geometry.scale,
            });

        this.currentSnapLineAnchorVertex = {
            x: point[0],
            y: point[1],
            element: circle,
            animate: this.createAnimate(circle),
        };
    }

    private hideVertexAlignmentLine(): void {
        if (this.currentSnapLineAnchorVertex === null) {
            return;
        }
        if (this.isDrawingAnnotation() === false || !this.enabledSnapLineByAnnotationVertex) {
            return;
        }
        this.currentSnapLineAnchorVertex.animate.stop();
        this.currentSnapLineAnchorVertex.animate = undefined;

        this.currentSnapLineAnchorVertex.element.remove();
        this.currentSnapLineAnchorVertex = null;
    }

    private createAnimate(circle: SVG.Element): SVG.Animation {
        const animate = circle.animate(1000, '<>', 0).attr({ r: this.minSnapPointRadius });
        animate.after(() => {
            circle.attr('r', this.maxSnapPointRadius);
            this.createAnimate(circle);
        });

        return animate;
    }
}
